import HelpParent from '@/pages/help-parent'
import Link from '@/components/link-component'
import Callout from '@/components/ui/callout'
import SocialMediaLinks from '@/components/social-media-links'
import DemosCta from '@/components/demos-cta-card'
import HelpSearch from '@/components/help-search'
import { TypeHelpArticle, TypeHelpNav } from '@/types/ctf'
import { useTranslations } from '@/utils/locale'
import { ThemeProvider } from '@/components/theme-provider'

const linkTileStyle =
  'w-full text-primaryBlue shadow-even-around-small dark:shadow-dark-even-around-small rounded-2xl p-3 border-2 border-white hover:border-primaryBlue hover:shadow-[0 0 1rem rgb(0 0 0 / 15%)] flex flex-col items-center flex-wrap dark:border-secondary-800 dark:text-white dark:hover:border-primaryBlue'
const tilesGrid = 'flex flex-wrap justify-between mb-10'

type HelpIndexProps = {
  data: TypeHelpArticle<'WITHOUT_UNRESOLVABLE_LINKS'>
  helpNav: TypeHelpNav<'WITHOUT_UNRESOLVABLE_LINKS'>
  newsletterForm: any
  footerNav: any
  seo: any
  app: any
  helpIndex: any
  headerBanners: any
}

export default function HelpIndex({
  seo,
  helpNav,
  footerNav,
  newsletterForm,
  helpIndex,
  headerBanners,
}: HelpIndexProps) {
  const {
    settings: { getStartedLinks, getStartedUserTypeLinks, secretsManagerLinks, tip, popularLinks },
  } = helpIndex?.fields || {}
  const { t } = useTranslations()

  const renderTileLink = (link: any, i: number) => (
    <Link to={link.path} className={linkTileStyle} key={`gsl-${i}`}>
      <div className="flex flex-col text-center lg:items-center">
        <span className="font-bwi text-[32px] leading-[60px] lg:mr-2 2xl:mr-0">
          {String.fromCodePoint(parseInt(link.icon, 16))}
        </span>
        <p className="m-0">{link.title}</p>
      </div>
    </Link>
  )
  return (
    <ThemeProvider>
      <HelpParent
        seo={seo}
        title="Help Center"
        outlineBody={undefined}
        helpNav={helpNav}
        footerNav={footerNav}
        newsletterForm={newsletterForm}
        headerBanners={headerBanners}
      >
        <div className="mt-5 lg:mt-1 mb-8 rounded-2xl px-6 py-6 bg-indigoBlue dark:bg-secondary-800">
          <h1 className="h3 text-white">{t('Welcome to the Help Center')}</h1>
          <HelpSearch searchSize="large" />
        </div>
        <div className="my-8 mr-0 lg:mr-4">
          {getStartedLinks && (
            <>
              <h4>{getStartedLinks.title}</h4>
              <div className={tilesGrid}>
                {getStartedLinks.links.map((link: any, i: number) => (
                  <div key={`t1-${i}`} className="flex w-[48%] xl:w-[18.5%] my-2">
                    {renderTileLink(link, i)}
                  </div>
                ))}
              </div>

              <h4>{getStartedUserTypeLinks.title}</h4>
              <div className={tilesGrid}>
                {getStartedUserTypeLinks.links.map((link: any, i: number) => (
                  <div key={`t2-${i}`} className="flex w-[48%] xl:w-[23.5%] my-2">
                    {renderTileLink(link, i)}
                  </div>
                ))}
              </div>

              <h4>{secretsManagerLinks.title}</h4>
              <div className={tilesGrid}>
                {secretsManagerLinks.links.map((link: any, i: number) => (
                  <div key={`t3-${i}`} className="flex w-[49%] my-2">
                    {renderTileLink(link, i)}
                  </div>
                ))}
              </div>

              <div className="hidden lg:block lg:mb-10">
                <Callout contentful_id="help-home" type="success">
                  <p>{tip}</p>
                </Callout>
              </div>

              <div className="w-full xl:flex xl:gap-1.5 xl:flex-row mb-10">
                {popularLinks.map((linksSection: any, index: number) => (
                  <div className="mb-10 xl:w-1/2 xl:mb-0" key={`pl-${index}`}>
                    <h5 className="mb-0">{linksSection.title}</h5>
                    <div className="flex flex-col">
                      {linksSection.links.map((link: any, i: number) => (
                        <Link key={`pl-l-${i}`} to={link.path} className="blue-link">
                          {link.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <DemosCta className="mb-8 dark:bg-secondary-800" />
              <SocialMediaLinks className="bg-white dark:bg-transparent p-0" />
            </>
          )}
        </div>
      </HelpParent>
    </ThemeProvider>
  )
}
